import { GridReveal, type PreparedRevealPlan } from "./GridReveal";
import { GridPreSpinDrop } from "./GridPreSpinDrop";
import type { CascadeMotionTuning } from "./CascadeMotionTuning";
import { GravityDrop, MaskRemoval } from "@slot/presentation";
import type { GridLayer, GridModel, MaskRemovalOptions, PreparedDropPlan, PresentationBarrier, PresentationTaskRunner, SymbolRemovalBurstSpawner, SymbolRemovalEffect, SymbolView } from "@slot/presentation";

export interface CascadeMechanicDeps {
  /** Optional only for headless tests: without a runner every flow completes instantly. */
  taskRunner?: PresentationTaskRunner | null;
  motionTuning: CascadeMotionTuning;
  /** While true, drop-out + reveal timings are divided by `bonusMode.speedMultiplier`. */
  isFeatureActive?: () => boolean;
  /** The burst played on each removed cell; null → plain shrink. */
  removalEffectSpawner?: SymbolRemovalBurstSpawner | null;
  /** The per-symbol removal clip (the symbol's own Spine `out`); a cell it declines shrinks and bursts. */
  removalEffect?: SymbolRemovalEffect | null;
}

/**
 * The cascade mechanic over a `GridLayer`: the pre-spin drop-out, the reveal drop-in, the
 * post-removal cascade and the mask removal (plan step 5, pass 4).
 *
 * Between a drop-out and the reveal that follows, the OUTGOING views (the previous grid, falling
 * off) are tracked here while the layer's cells already map to the INCOMING views the reveal
 * prepared; the outgoing views are released when the drop-out completes (or, without one, when
 * the reveal has landed).
 */
export class CascadeMechanic {
  /** The cascade drop's own scope (the reveal runs under `reveal_motion`, the drop-out under `prespin_motion`). */
  public static readonly CASCADE_SCOPE = "cascade_motion";

  private readonly grid: GridLayer;
  private readonly taskRunner: PresentationTaskRunner | null;
  private readonly motionTuning: CascadeMotionTuning;
  private readonly isFeatureActive: () => boolean;
  private readonly removal: MaskRemoval;

  private readonly reveal: GridReveal;
  private readonly cascadeDrop: GravityDrop;
  private readonly preSpinDrop = new GridPreSpinDrop();

  /** The previous grid's views while they leave (drop-out) or wait to be replaced (reveal without one). */
  private outgoing: Map<number, SymbolView> | null = null;
  private dropOutInFlight = false;
  private readonly symbolEnterGridCallbacks: Map<number, () => void> = new Map();

  public constructor(grid: GridLayer, deps: CascadeMechanicDeps) {
    this.grid = grid;
    this.taskRunner = deps.taskRunner ?? null;
    this.motionTuning = deps.motionTuning;
    this.isFeatureActive = deps.isFeatureActive ?? (() => false);
    this.removal = new MaskRemoval({
      grid,
      scope: CascadeMechanic.CASCADE_SCOPE,
      taskRunner: this.taskRunner,
      burstSpawner: deps.removalEffectSpawner ?? null,
      symbolEffect: deps.removalEffect ?? null,
      releaseReason: "remove_by_mask",
    });
    this.reveal = new GridReveal({ pool: grid.pool, layoutUtil: grid.layoutUtil });
    this.cascadeDrop = new GravityDrop({ cells: grid, pool: grid.pool, layoutUtil: grid.layoutUtil, scope: CascadeMechanic.CASCADE_SCOPE });
  }

  /**
   * Register a one-shot callback for the NEXT reveal or cascade: fired when the symbol landing at
   * `index` crosses the grid's top edge (a game hooks a stinger or a VFX to it). Consumed by the
   * next animate call; unused registrations are dropped there.
   */
  public onNextRevealSymbolEntersGrid(index: number, callback: () => void): void {
    this.symbolEnterGridCallbacks.set(index, callback);
  }

  // ── Plain render / reset ──────────────────────────────────────────────────────────────────

  /** Paint a model with no motion (boot, recovery): any pending transition is discarded first. */
  public renderModel(model: GridModel): void {
    this.releaseOutgoingOffGrid();
    this.grid.renderGridModel(model);
  }

  /** Grid recovery: release the transition views and every cell. */
  public resetPresentationState(): void {
    this.releaseOutgoingOffGrid();
    this.symbolEnterGridCallbacks.clear();
    this.grid.resetPresentationState();
  }

  // ── Pre-spin drop-out ─────────────────────────────────────────────────────────────────────

  /**
   * The current grid falls off the bottom and recycles. Releases `barrier`'s "spin" key the tuning's
   * `dropout.revealDelaySeconds` after the last symbol has started falling (decision 48), so the reveal can
   * prepare while the tail still drops. Clears the grid's model only when it emptied the grid: a reveal
   * prepared during the fall owns the cells and commits its own model when it lands, which may be before
   * this tail ends (decision 33).
   */
  public async dropOutDownAndRecycle(input: { presentationBarrier: PresentationBarrier }): Promise<void> {
    this.grid.ensureLayout();
    const outgoing = this.beginTransition();
    const divisor = this.bonusSpeedDivisor();
    const t = this.motionTuning.dropout;

    this.dropOutInFlight = true;
    try {
      await this.preSpinDrop.dropOutDownAndRecycle({
        symbolsByIndex: outgoing,
        layoutUtil: this.grid.layoutUtil,
        layout: this.grid.getComputedLayout(),
        gridColumnCount: this.grid.gridColumnCount,
        gridRowCount: this.grid.gridRowCount,
        runner: this.taskRunner,
        durationSeconds: t.durationSeconds / divisor,
        columnStaggerSeconds: t.columnStaggerSeconds / divisor,
        withinColumnStaggerSeconds: t.withinColumnStaggerSeconds / divisor,
        dropDistanceRows: t.dropDistanceRows,
        easing: t.easing,
        barrier: input.presentationBarrier,
        barrierKey: "spin",
        releaseDelaySeconds: t.revealDelaySeconds / divisor,
      });
    } finally {
      this.dropOutInFlight = false;
    }

    this.releaseOutgoing();
    if (this.grid.cellSnapshot().size === 0) this.grid.commitModel(null);
  }

  // ── Reveal (slam affects only the reveal scope) ───────────────────────────────────────────

  /** Spawn the next board above the grid and bind it as the cells; nothing moves yet. */
  public prepareRevealToModel(input: { nextModel: GridModel; strictSymbolConsistencyCheck?: boolean }): PreparedRevealPlan {
    this.grid.ensureLayout();
    this.beginTransition();
    const divisor = this.bonusSpeedDivisor();
    const t = this.motionTuning.reveal;

    const plan = this.reveal.prepareRevealToModel({
      nextModel: input.nextModel,
      gridColumnCount: this.grid.gridColumnCount,
      gridRowCount: this.grid.gridRowCount,
      layout: this.grid.getComputedLayout(),
      dropDurationSeconds: t.dropDurationSeconds / divisor,
      columnStaggerSeconds: t.columnStaggerSeconds / divisor,
      withinColumnStaggerSeconds: t.withinColumnStaggerSeconds / divisor,
      spawnStartOffsetRows: t.spawnStartOffsetRows,
      strictSymbolConsistencyCheck: input.strictSymbolConsistencyCheck,
    });

    // The incoming views ARE the cells from here on (a frame_prepare action reads them).
    this.grid.forgetAll();
    for (const [index, view] of plan.newSymbolsByIndex.entries()) this.grid.adopt(index, view);
    return plan;
  }

  public async animatePreparedReveal(plan: PreparedRevealPlan): Promise<void> {
    this.grid.notifySequenceStart();
    const enterCallbacks = this.takeEnterCallbacks();

    await this.reveal.animatePreparedReveal({
      plan,
      runner: this.taskRunner,
      gridColumnCount: this.grid.gridColumnCount,
      gridRowCount: this.grid.gridRowCount,
      layout: this.grid.getComputedLayout(),
      strictGetSymbolIdAtIndex: (index) => this.grid.getSymbolViewAtIndex(index)?.getSymbolId() ?? null,
      onColumnDropLanded: (column) => this.grid.notifyColumnLanded(column),
      symbolEnterGridCallbacks: enterCallbacks,
      enableOvershoot: this.motionTuning.reveal.overshoot.enable,
      overshootPixels: this.motionTuning.reveal.overshoot.pixels,
      overshootPhaseRatio: this.motionTuning.reveal.overshoot.phaseRatio,
      overshootSettleEasing: this.motionTuning.reveal.overshoot.settleEasing,
    });

    this.grid.commitModel(plan.nextModel);
    // A reveal with no drop-out in front of it (nothing fell off): the previous views are recycled now.
    if (!this.dropOutInFlight) this.releaseOutgoing();
  }

  // ── Cascade (NOT affected by slam) ────────────────────────────────────────────────────────

  /**
   * Plan the cascade against the removal mask + next grid; the cells are re-mapped immediately.
   * With no rendered grid to cascade from (defensive), the target is rendered directly.
   */
  public prepareCascadeToModel(input: {
    removedPositionMask: string;
    nextModel: GridModel;
    fallDurationSeconds?: number;
    spawnDurationSeconds?: number;
    columnStaggerSeconds?: number;
    withinColumnStaggerSeconds?: number;
    strictSymbolConsistencyCheck?: boolean;
  }): PreparedDropPlan {
    this.grid.ensureLayout();

    if (!this.grid.lastModel) {
      this.grid.renderGridModel(input.nextModel);
      return GravityDrop.instantPlan(input.nextModel, this.grid.cellSnapshot(), input);
    }

    return this.cascadeDrop.prepareDropToModel({
      removedPositionMask: input.removedPositionMask,
      nextModel: input.nextModel,
      gridColumnCount: this.grid.gridColumnCount,
      gridRowCount: this.grid.gridRowCount,
      layout: this.grid.getComputedLayout(),
      fallDurationSeconds: input.fallDurationSeconds,
      spawnDurationSeconds: input.spawnDurationSeconds,
      columnStaggerSeconds: input.columnStaggerSeconds,
      withinColumnStaggerSeconds: input.withinColumnStaggerSeconds,
      strictSymbolConsistencyCheck: input.strictSymbolConsistencyCheck,
    });
  }

  public async animatePreparedCascade(plan: PreparedDropPlan): Promise<void> {
    this.grid.notifySequenceStart();
    const enterCallbacks = this.takeEnterCallbacks();
    const overshoot = this.motionTuning.cascade.overshoot;

    await this.cascadeDrop.animatePreparedDrop({
      plan,
      runner: this.taskRunner,
      gridColumnCount: this.grid.gridColumnCount,
      gridRowCount: this.grid.gridRowCount,
      layout: this.grid.getComputedLayout(),
      enableOvershoot: overshoot.enable,
      overshootPixels: overshoot.pixels,
      overshootPhaseRatio: overshoot.phaseRatio,
      overshootSettleEasing: overshoot.settleEasing,
      // A chain drop: the pitched landing stinger is reserved for the initial reveal.
      onColumnDropLanded: (column) => this.grid.notifyColumnLanded(column, { isChainDrop: true }),
      symbolEnterGridCallbacks: enterCallbacks,
      strictVerifyIdAtIndex: (index) => this.grid.getSymbolViewAtIndex(index)?.getSymbolId() ?? null,
    });

    this.grid.commitModel(plan.nextModel);
  }

  /** Convenience: prepare + animate in one call. */
  public async cascadeToModel(input: {
    removedPositionMask: string;
    nextModel: GridModel;
    fallDurationSeconds?: number;
    spawnDurationSeconds?: number;
    columnStaggerSeconds?: number;
    withinColumnStaggerSeconds?: number;
    strictSymbolConsistencyCheck?: boolean;
  }): Promise<void> {
    const plan = this.prepareCascadeToModel(input);
    await this.animatePreparedCascade(plan);
  }

  /** The masked (winning) cells leave the grid (see `MaskRemoval`); the collapse event then drives the drop. */
  public removeByMask(mask: string, options: MaskRemovalOptions): Promise<void> {
    return this.removal.removeByMask(mask, options);
  }

  // ── Internals ─────────────────────────────────────────────────────────────────────────────

  private bonusSpeedDivisor(): number {
    return this.isFeatureActive() ? this.motionTuning.bonusMode.speedMultiplier : 1;
  }

  private takeEnterCallbacks(): Map<number, () => void> | undefined {
    if (this.symbolEnterGridCallbacks.size === 0) return undefined;
    const taken = new Map(this.symbolEnterGridCallbacks);
    this.symbolEnterGridCallbacks.clear();
    return taken;
  }

  /** Start tracking the current cells as outgoing (a no-op while a transition is already open). */
  private beginTransition(): Map<number, SymbolView> {
    if (!this.outgoing) this.outgoing = this.grid.cellSnapshot();
    return this.outgoing;
  }

  /** Release every outgoing view — through its cell while it still is one, directly otherwise. */
  private releaseOutgoing(): void {
    const outgoing = this.outgoing;
    if (!outgoing) return;
    this.outgoing = null;
    const leaving = new Set(outgoing.values());
    for (const [index, view] of this.grid.cellSnapshot()) {
      if (!leaving.has(view)) continue;
      this.grid.releaseAt(index, "dropout_recycle");
      leaving.delete(view);
    }
    for (const view of leaving) {
      try {
        this.grid.releaseView(view, "dropout_recycle");
      } catch {
        // Already released (e.g. a pool-unsafe view destroyed mid-transition).
      }
    }
  }

  /** Release only the outgoing views that are NOT cells any more (a render / reset handles the cells). */
  private releaseOutgoingOffGrid(): void {
    const outgoing = this.outgoing;
    if (!outgoing) return;
    this.outgoing = null;
    const cells = new Set(this.grid.views());
    for (const view of new Set(outgoing.values())) {
      if (cells.has(view)) continue;
      try {
        this.grid.releaseView(view, "transition_discard");
      } catch {
        // Already released.
      }
    }
  }
}
